import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import { useAuth } from '../contexts/AuthContext';
import { useMessage } from '../contexts/MessageContext';
import { Loading } from './common/Loading';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const InventoryCalendar = () => {
  const [inventory, setInventory] = useState([]);
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const { getToken, logout } = useAuth();
  const { showError } = useMessage();

  useEffect(() => {
    const token = getToken ? getToken() : localStorage.getItem('token');
    if (!token) {
      logout();
      showError('Authentication required. Please log in.');
      setLoading(false);
      return;
    }

    axios
      .get(`${apiUrl}/inventory`, { headers: { 'x-auth-token': token } })
      .then((res) => {
        const items = res.data?.success && Array.isArray(res.data.data)
          ? res.data.data
          : Array.isArray(res.data?.items)
            ? res.data.items
            : Array.isArray(res.data)
              ? res.data
              : [];
        setInventory(items.filter((item) => item?.uuid && item?.name));
        setLoading(false);
      })
      .catch(() => {
        showError('Failed to load inventory list');
        setLoading(false);
      });
  }, [getToken, logout, showError]);

  useEffect(() => {
    if (!selectedInventoryId) {
      setEvents([]);
      return;
    }

    const token = getToken ? getToken() : localStorage.getItem('token');
    if (!token) {
      logout();
      showError('Authentication required. Please log in.');
      return;
    }

    // Fetch a 3-month window centered on the currently navigated month.
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
    const startStr = start.toISOString().slice(0, 10);
    const endStr = end.toISOString().slice(0, 10);

    setLoading(true);
    axios
      .get(
        `${apiUrl}/inventory/availability/daily?inventoryId=${selectedInventoryId}&startDate=${startStr}&endDate=${endStr}`,
        { headers: { 'x-auth-token': token } }
      )
      .then((res) => {
        const days = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        const evts = days.map((day) => {
          const d = new Date(day.date);
          const title = `Booked ${day.bookedQuantity || 0} / Avail ${day.availableQuantity ?? day.totalQuantity ?? 0}`;
          return {
            id: `${selectedInventoryId}-${day.date}`,
            title,
            start: d,
            end: d,
            allDay: true,
            resource: day,
          };
        });
        setEvents(evts);
        setLoading(false);
      })
      .catch(() => {
        showError('Failed to load availability details');
        setEvents([]);
        setLoading(false);
      });
  }, [selectedInventoryId, currentDate, getToken, logout, showError]);

  if (loading && inventory.length === 0) return <Loading />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Inventory Availability Calendar</h1>

      <div className="mb-4 bg-white p-4 rounded shadow">
        <label htmlFor="inventory-select" className="block text-sm font-medium text-gray-700 mb-1">
          Inventory item
        </label>
        <select
          id="inventory-select"
          className="w-full max-w-md border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
          value={selectedInventoryId}
          onChange={(e) => setSelectedInventoryId(e.target.value)}
        >
          <option value="">Select an item to view availability</option>
          {inventory.map((item) => (
            <option key={item.uuid} value={item.uuid}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded shadow p-4" style={{ height: '700px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={['month', 'week', 'day']}
          defaultView="month"
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          style={{ height: '100%' }}
        />
      </div>
    </div>
  );
};

export default InventoryCalendar;
